import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import CopyButton from "../components/CopyButton";
import BackButton from "../components/BackButton";
import LanguageSwitcher from "../components/LanguageSwitcher";

import {
  Beaker, FlaskConical, MessageSquare, FileText, Database, LogOut,
  Loader2, Sparkles, Brain, Upload, Send, Trash2, Download,
  History, FileUp, Paperclip, Shield, Star
} from "lucide-react";
import ResizableSplit from "../components/ResizableSplit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const elastomerTypes = [
  { value: "any", label: "Any (AI Recommended)" },
  { value: "NR", label: "Natural Rubber (NR)" },
  { value: "SBR", label: "Styrene-Butadiene (SBR)" },
  { value: "NBR", label: "Nitrile Rubber (NBR)" },
  { value: "EPDM", label: "EPDM" },
  { value: "CR", label: "Neoprene (CR)" },
  { value: "Silicone", label: "Silicone Rubber" },
  { value: "FKM", label: "Fluoroelastomer (FKM)" },
  { value: "IIR", label: "Butyl Rubber (IIR)" },
  { value: "PU", label: "Polyurethane (PU)" },
];

const resistanceLevels = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "excellent", label: "Excellent" },
];

const blankForm = {
  hardness_shore_a: "", tensile_strength_mpa: "", elongation_percent: "",
  compression_set_percent: "", tear_strength_kn_m: "", abrasion_resistance: "",
  oil_resistance: "", heat_resistance_c: "", ozone_resistance: "",
  elastomer_type: "", application: "", additional_requirements: "",
};

export default function FormulationPage() {
  const { user, logout } = useAuth();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { fid: urlFid } = useParams();

  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractFileName, setExtractFileName] = useState("");
  const [uploadPct, setUploadPct] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [formData, setFormData] = useState(blankForm);
  const [result, setResult] = useState(null);
  const [resultId, setResultId] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("input"); // input | history
  const [histFilter, setHistFilter] = useState("all");

  const toggleFavorite = async (f) => {
    try {
      const r = await axios.patch(`${API}/formulation-history/${f.id}/favorite`);
      setHistory((h) => h.map((x) => (x.id === f.id ? { ...x, is_favorite: r.data.is_favorite } : x)));
    } catch (e) {}
  };
  const [chatInput, setChatInput] = useState("");
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => { fetchHistory(); }, []);
  useEffect(() => { if (urlFid) loadFormulation(urlFid); }, [urlFid]);

  const fetchHistory = async () => {
    try { const r = await axios.get(`${API}/formulation-history`); setHistory(r.data); } catch (e) {}
  };

  const loadFormulation = async (fid) => {
    try {
      const r = await axios.get(`${API}/formulation-history/${fid}`);
      const f = r.data;
      setResultId(f.id);
      setResult(f.response);
      setFormData({ ...blankForm, ...Object.fromEntries(Object.entries(f.request || {}).map(([k, v]) => [k, v == null ? "" : v])) });
      setChatMsgs(f.chat_messages || []);
    } catch (e) {}
  };

  const handleChange = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setResult(null);
    setResultId(null);
    setChatMsgs([]);
    try {
      const payload = {};
      Object.entries(formData).forEach(([k, v]) => {
        if (v === "" || v == null) return;
        if (["hardness_shore_a", "tensile_strength_mpa", "elongation_percent", "compression_set_percent", "tear_strength_kn_m", "heat_resistance_c"].includes(k)) {
          payload[k] = parseFloat(v);
        } else {
          payload[k] = v;
        }
      });
      payload.language = (i18n.language || "en").startsWith("de") ? "de" : "en";
      const res = await axios.post(`${API}/predict-formulation`, payload);
      setResult(res.data.formulation);
      setResultId(res.data.id);
      fetchHistory();
    } catch (err) {
      setResult("Error generating formulation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const autofillFromFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    setExtractFileName(file.name);
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/predict-formulation/from-file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadPct(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      const extracted = r.data.extracted || {};
      setFormData((p) => ({ ...p, ...Object.fromEntries(Object.entries(extracted).map(([k, v]) => [k, v == null ? "" : v])) }));
    } catch (err) {
      alert("Could not extract data from file");
    } finally {
      setExtracting(false);
      setExtractFileName("");
      setUploadPct(0);
      e.target.value = "";
    }
  };

  const deleteFormulation = async (fid) => {
    if (!window.confirm("Delete this formulation?")) return;
    try {
      await axios.delete(`${API}/formulation-history/${fid}`);
      setHistory((h) => h.filter((x) => x.id !== fid));
      if (resultId === fid) { setResult(null); setResultId(null); }
    } catch (e) {}
  };

  const clearAll = async () => {
    if (history.length === 0) return;
    if (!window.confirm(`Delete ALL ${history.length} formulations? This cannot be undone.`)) return;
    try {
      await Promise.all(history.map((f) => axios.delete(`${API}/formulation-history/${f.id}`)));
      setHistory([]);
      setResult(null); setResultId(null);
    } catch (e) { alert("Some formulations could not be deleted"); fetchHistory(); }
  };

  const exportPdf = async () => {
    if (!resultId) return;
    setExporting(true);
    try {
      const r = await axios.get(`${API}/formulation/${resultId}/export-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `formulation_${resultId.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {}
    finally { setExporting(false); }
  };

  const sendChatMsg = async () => {
    if (!chatInput.trim() || !resultId || chatLoading) return;
    const m = chatInput.trim(); setChatInput("");
    setChatMsgs((p) => [...p, { role: "user", content: m }]);
    setChatLoading(true);
    try {
      const r = await axios.post(`${API}/formulation/${resultId}/chat`, {
        formulation_id: resultId,
        message: m,
        language: (i18n.language || "en").startsWith("de") ? "de" : "en",
      });
      setChatMsgs((p) => [...p, { role: "assistant", content: r.data.response }]);
    } catch (e) {
      setChatMsgs((p) => [...p, { role: "assistant", content: "Error generating reply." }]);
    } finally {
      setChatLoading(false);
    }
  };

  const navItems = [
    { icon: MessageSquare, label: "Chat", path: "/chat" },
    { icon: FlaskConical, label: "Formulation", path: "/formulation" },
    { icon: FileText, label: "Documents", path: "/documents" },
    { icon: Database, label: "Knowledge", path: "/knowledge" },
    { icon: Brain, label: "Training", path: "/training" },
    ...(user?.is_admin ? [{ icon: Shield, label: "Admin", path: "/admin" }] : []),
  ];

  return (
    <div className="flex h-screen bg-slate-50">
      <div className="w-72 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center"><Beaker className="w-5 h-5 text-white" /></div>
            <span className="font-heading font-bold text-lg text-slate-900">ElastoAI</span>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                item.path === "/formulation" ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}>
              <item.icon className="w-5 h-5" /><span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
            <span className="text-sm font-medium text-slate-600">{user?.name?.charAt(0).toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p></div>
          <button onClick={logout} className="p-2 hover:bg-slate-100 rounded-lg"><LogOut className="w-4 h-4 text-slate-500" /></button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-6 gap-3">
          <BackButton />
          <FlaskConical className="w-5 h-5 text-orange-500 ml-2" />
          <h1 className="font-heading font-semibold text-slate-900">{t("formulation.title")}</h1>
          <div className="flex-1" />
          <LanguageSwitcher className="mr-3" />
          <div className="flex items-center gap-2 bg-slate-100 rounded-full p-1">
            <button onClick={() => setActiveTab("input")} data-testid="tab-input"
              className={`px-4 py-1.5 rounded-full text-sm font-medium ${activeTab === "input" ? "bg-white text-slate-900 shadow" : "text-slate-600"}`}>{t("formulation.designer")}</button>
            <button onClick={() => navigate("/forward-prediction")} data-testid="tab-forward"
              className="px-4 py-1.5 rounded-full text-sm font-medium text-slate-600 hover:text-slate-900">
              {t("formulation.forwardPrediction")}
            </button>
            <button onClick={() => setActiveTab("history")} data-testid="tab-history"
              className={`px-4 py-1.5 rounded-full text-sm font-medium ${activeTab === "history" ? "bg-white text-slate-900 shadow" : "text-slate-600"}`}>
              {t("formulation.history")} ({history.length})
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-6xl mx-auto">
            {activeTab === "history" ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-heading text-xl font-bold text-slate-900">Saved Formulations ({history.length})</h2>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1">
                      <button onClick={() => setHistFilter("all")} data-testid="form-hist-filter-all"
                        className={`px-3 py-1 rounded-full text-xs font-medium ${histFilter === "all" ? "bg-white shadow text-slate-900" : "text-slate-600"}`}>
                        All ({history.length})
                      </button>
                      <button onClick={() => setHistFilter("fav")} data-testid="form-hist-filter-fav"
                        className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${histFilter === "fav" ? "bg-white shadow text-slate-900" : "text-slate-600"}`}>
                        <Star className="w-3 h-3 text-amber-500 fill-amber-500" /> Favorites ({history.filter((f) => f.is_favorite).length})
                      </button>
                    </div>
                    {history.length > 0 && (
                      <Button variant="outline" onClick={clearAll} data-testid="clear-all-formulations-btn" className="text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200">
                        <Trash2 className="w-4 h-4 mr-2" /> Clear All History
                      </Button>
                    )}
                  </div>
                </div>
                {(histFilter === "fav" ? history.filter((f) => f.is_favorite) : history).length === 0 ? (
                  <div className="text-center py-12 text-slate-500">{histFilter === "fav" ? "No favorites yet — click the star on any saved formulation" : "No formulations yet"}</div>
                ) : (histFilter === "fav" ? history.filter((f) => f.is_favorite) : history).map((f) => (
                  <div key={f.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4 hover:border-orange-300 transition-colors" data-testid={`history-${f.id}`}>
                    <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center">
                      <FlaskConical className="w-6 h-6 text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900">{f.request?.elastomer_type || "Custom"} — {f.request?.application || "General"}</p>
                      <p className="text-sm text-slate-500">
                        {f.request?.hardness_shore_a && `${f.request.hardness_shore_a} Shore A • `}
                        {new Date(f.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button onClick={() => toggleFavorite(f)} data-testid={`form-fav-${f.id}`}
                      title={f.is_favorite ? "Remove from favorites" : "Mark as favorite"}
                      className="p-2 rounded-lg hover:bg-amber-50">
                      <Star className={`w-5 h-5 ${f.is_favorite ? "text-amber-500 fill-amber-500" : "text-slate-300"}`} />
                    </button>
                    <Button variant="outline" onClick={() => { navigate(`/formulation/${f.id}`); setActiveTab("input"); }} data-testid={`open-${f.id}`}>Open</Button>
                    <Button variant="outline" onClick={() => deleteFormulation(f.id)} data-testid={`delete-${f.id}`} className="text-red-500 hover:text-white hover:bg-red-500 border-red-200">
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <ResizableSplit storageKey="formulation-split">
                {/* Input — sticky so it stays visible while scrolling the long result on the right */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6 lg:sticky lg:top-0 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center"><Sparkles className="w-5 h-5 text-white" /></div>
                      <div>
                        <h2 className="font-heading font-bold text-slate-900">Target Properties</h2>
                        <p className="text-sm text-slate-500">Manually input or auto-fill from a file</p>
                      </div>
                    </div>
                    <label className="cursor-pointer">
                      <input type="file" className="hidden" accept=".pdf,.xlsx,.xls,.png,.jpg,.jpeg,.webp" onChange={autofillFromFile} data-testid="formulation-autofill" disabled={extracting} />
                      <span className={`inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full ${extracting ? "bg-blue-50 text-blue-700" : "bg-orange-50 text-orange-700 hover:bg-orange-100"}`}>
                        {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
                        {extracting ? (uploadPct < 100 ? `Uploading ${uploadPct}%` : "Extracting properties...") : "Auto-fill from file"}
                      </span>
                    </label>
                  </div>

                  {extracting && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl" data-testid="formulation-upload-progress">
                      <div className="flex items-center gap-3">
                        <Loader2 className="w-5 h-5 animate-spin text-blue-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-blue-900 truncate">
                            {uploadPct < 100 ? `Uploading ${extractFileName}…` : `Extracting target properties from ${extractFileName}…`}
                          </p>
                          <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden mt-1">
                            <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${uploadPct < 100 ? uploadPct : 100}%`, opacity: uploadPct < 100 ? 1 : 0.7 }} />
                          </div>
                        </div>
                        <span className="text-xs text-blue-700 font-mono flex-shrink-0">{uploadPct < 100 ? `${uploadPct}%` : "AI"}</span>
                      </div>
                      <p className="text-xs text-blue-600 mt-2">Please wait — this may take 10-30 seconds depending on file size.</p>
                    </div>
                  )}

                  <form onSubmit={submit} className="space-y-6">
                    <div>
                      <h3 className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">Mechanical</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          ["hardness_shore_a", "Hardness (Shore A)", "65"],
                          ["tensile_strength_mpa", "Tensile (MPa)", "15"],
                          ["elongation_percent", "Elongation (%)", "400"],
                          ["compression_set_percent", "Compression Set (%)", "25"],
                          ["tear_strength_kn_m", "Tear (kN/m)", "30"],
                          ["heat_resistance_c", "Heat Resistance (°C)", "120"],
                        ].map(([k, lbl, ph]) => (
                          <div key={k} className="space-y-1">
                            <Label className="text-xs text-slate-600">{lbl}</Label>
                            <Input type="number" step="0.1" value={formData[k]} onChange={(e) => handleChange(k, e.target.value)} placeholder={ph} className="h-10 bg-slate-50 border-slate-200" data-testid={`input-${k}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">Resistance</h3>
                      <div className="grid grid-cols-3 gap-3">
                        {[["abrasion_resistance", "Abrasion"], ["oil_resistance", "Oil"], ["ozone_resistance", "Ozone"]].map(([k, lbl]) => (
                          <div key={k} className="space-y-1">
                            <Label className="text-xs text-slate-600">{lbl}</Label>
                            <Select value={formData[k]} onValueChange={(v) => handleChange(k, v)}>
                              <SelectTrigger className="h-10 bg-slate-50 border-slate-200" data-testid={`select-${k}`}><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent>{resistanceLevels.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-600">Preferred Elastomer</Label>
                        <Select value={formData.elastomer_type} onValueChange={(v) => handleChange("elastomer_type", v)}>
                          <SelectTrigger className="h-10 bg-slate-50 border-slate-200" data-testid="select-elastomer-type"><SelectValue placeholder="Select or let AI decide" /></SelectTrigger>
                          <SelectContent>{elastomerTypes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-600">Application</Label>
                        <Input value={formData.application} onChange={(e) => handleChange("application", e.target.value)} placeholder="e.g., Automotive fuel hose, conveyor belt..." className="h-10 bg-slate-50 border-slate-200" data-testid="input-application" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-600">Additional Requirements</Label>
                        <Textarea value={formData.additional_requirements} onChange={(e) => handleChange("additional_requirements", e.target.value)} placeholder="Any constraints, preferences, or specifications..." className="min-h-[70px] bg-slate-50 border-slate-200" data-testid="input-additional" />
                      </div>
                    </div>
                    <Button type="submit" disabled={loading} data-testid="predict-btn"
                      className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-medium">
                      {loading ? (<><Loader2 className="w-5 h-5 animate-spin mr-2" />Generating...</>) : (<><FlaskConical className="w-5 h-5 mr-2" />Predict Formulation</>)}
                    </Button>
                  </form>
                </div>

                {/* Result + Chat — fixed-height flex column so the chat input sticks at bottom */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col lg:sticky lg:top-0 lg:h-[calc(100vh-7rem)]">
                  <div className="flex items-center justify-between mb-4 flex-shrink-0">
                    <h2 className="font-heading font-bold text-slate-900">Result</h2>
                    <div className="flex items-center gap-2">
                      {result && <CopyButton text={result} label />}
                      {resultId && (
                        <Button variant="outline" size="sm" onClick={exportPdf} disabled={exporting} data-testid="export-pdf-btn">
                          {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                          Export PDF
                        </Button>
                      )}
                      {resultId && (
                        <Button variant="ghost" size="sm" onClick={() => deleteFormulation(resultId)} className="text-red-500" data-testid="delete-formulation-btn">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto pr-2 -mr-2">
                    {loading ? (
                      <div className="flex flex-col items-center justify-center h-64">
                        <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-4" />
                        <p className="text-slate-500">Analyzing requirements...</p>
                      </div>
                    ) : result ? (
                      <div className="prose prose-sm max-w-none break-words prose-p:my-3 prose-headings:mt-5 prose-headings:mb-2 prose-h2:text-base prose-h2:font-semibold prose-h3:text-sm prose-h3:font-semibold prose-ul:my-3 prose-ul:pl-5 prose-li:my-1 prose-strong:text-slate-900 prose-strong:font-semibold prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none" data-testid="formulation-result">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                          table: (p) => (
                            <div className="overflow-x-auto my-3 -mx-1 px-1">
                              <table className="w-full border-collapse text-xs" {...p} />
                            </div>
                          ),
                          th: (p) => <th className="border border-slate-300 px-2 py-1.5 bg-slate-100 text-left font-semibold" {...p} />,
                          td: (p) => <td className="border border-slate-300 px-2 py-1.5 align-top" {...p} />,
                          img: (p) => <img {...p} alt={p.alt || ""} style={{ maxWidth: "100%", borderRadius: 8, marginTop: 8 }} />,
                        }}>{result}</ReactMarkdown>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-64 text-center">
                        <FlaskConical className="w-12 h-12 text-slate-300 mb-3" />
                        <p className="text-slate-500">Enter properties or upload a file, then click Predict.</p>
                      </div>
                    )}

                    {resultId && chatMsgs.length > 0 && (
                      <div className="mt-6 pt-6 border-t border-slate-200 space-y-3">
                        <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" /> Conversation
                        </h3>
                        {chatMsgs.map((m, i) => (
                          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} group`}>
                            <div className={`px-3 py-2 rounded-lg text-sm ${m.role === "user" ? "bg-slate-900 text-white max-w-[85%]" : "bg-slate-100 text-slate-900 max-w-full w-full"}`}>
                              {m.role === "assistant" ? (
                                <div className="prose prose-sm max-w-none break-words prose-p:my-3 prose-headings:mt-4 prose-headings:mb-2">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                                    table: (p) => (
                                      <div className="overflow-x-auto my-2 -mx-1 px-1">
                                        <table className="w-full border-collapse text-xs" {...p} />
                                      </div>
                                    ),
                                    th: (p) => <th className="border border-slate-300 px-2 py-1 bg-slate-200 text-left font-semibold" {...p} />,
                                    td: (p) => <td className="border border-slate-300 px-2 py-1 align-top" {...p} />,
                                  }}>{m.content}</ReactMarkdown>
                                </div>
                              ) : (
                                <div>{m.content}</div>
                              )}
                              <div className="mt-1 opacity-0 group-hover:opacity-100"><CopyButton text={m.content} /></div>
                            </div>
                          </div>
                        ))}
                        {chatLoading && <div className="text-xs text-slate-500"><Loader2 className="w-3 h-3 inline animate-spin" /> Thinking...</div>}
                      </div>
                    )}
                  </div>

                  {resultId && (
                    <div className="mt-4 pt-4 border-t border-slate-200 flex-shrink-0">
                      <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" /> Chat with this Formulation
                      </h3>
                      <div className="flex gap-2 items-end">
                        <Textarea
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              sendChatMsg();
                            }
                          }}
                          placeholder="Ask anything about this formulation... (Enter to send, Shift+Enter for new line)"
                          rows={1}
                          className="bg-slate-50 border-slate-200 resize-none min-h-[44px] max-h-32 py-2.5"
                          data-testid="formulation-chat-input"
                        />
                        <Button size="sm" onClick={sendChatMsg} disabled={chatLoading || !chatInput.trim()} data-testid="formulation-chat-send" className="bg-orange-500 hover:bg-orange-600 text-white h-11 px-4">
                          {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </ResizableSplit>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
